import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { UnreachableState } from '@/components/ui/UnreachableState/UnreachableState';
import { signIn } from '@/auth/authData';
import { getMe } from '@/api';
import { AUTH_ME_KEY } from '@/auth/authData';
import { queryClient as moduleQueryClient } from '@/api/queryClient';
import { retryQuery } from '@/api/restartQuery';
import { isTransportFailure } from '@/api/failureClass';
import { buttonLabels, formMessages, paths } from '@/constants';
import { vars } from '@/theme';
import { authRouteHref, returnAddress } from '@/features/accounts/lib/authRouteHref';
import { getDefaultRoute, getEffectiveLevel, isNoCompanyLogin } from '@/lib/defaultRoute';
import { ContextRequired } from '@/features/context';
import type { UserLevel } from '@/types';

// Module-level flag to track if dev refetch has already run this page load
let devRefetchCompleted = false;

// Dev mode: force refetch auth/me and config on every page refresh
const DEV_REFETCH_ON_REFRESH = import.meta.env.VITE_DEV_REFETCH_AUTH_ON_REFRESH === 'true';

/**
 * #447: choose the unreachable-state copy based on the /me failure class.
 * A network/transport failure → "Check your internet connection" (the user's
 * problem). A server response (429, 5xx) → "Ving isn't responding" (our
 * problem). Uses `isTransportFailure` from failureClass.ts.
 */
function meFailureMessage(error: unknown): string {
  if (isTransportFailure(error)) {
    return formMessages.connectivity.unreachable;
  }
  return formMessages.connectivity.serverNotResponding;
}

/** #447: Try again re-sends /me. #1561-d: through retryQuery, so a /me request still in flight is abandoned, not joined. */
function retryMe() {
  void retryQuery(moduleQueryClient, AUTH_ME_KEY);
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredLevel?: UserLevel[];
  requireSuperuser?: boolean;
  requireStaff?: boolean;
  requireNetworkContext?: boolean;
}

export function ProtectedRoute({
  children,
  requiredLevel,
  requireSuperuser,
  requireStaff,
  requireNetworkContext,
}: ProtectedRouteProps) {
  const location = useLocation();
  const { user, isSuperuser, isStaff, hasSession, isLoadingUser, userLoadError } = useCurrentUser();
  const queryClient = useQueryClient();

  // Dev mode: refetch auth/me and config on every page refresh for easier debugging
  useEffect(() => {
    if (DEV_REFETCH_ON_REFRESH && user && !devRefetchCompleted) {
      devRefetchCompleted = true;
      console.warn('[DEV] Refetching /auth/me and /api/config on page refresh');

      // Preserve existing token since /auth/me doesn't return it
      const existingToken = user.token;

      // Refetch user data
      getMe()
        .then((userData) => {
          console.warn('[DEV] Refreshed user data:', userData);
          // Merge with existing token to avoid logout
          signIn({ ...userData, token: existingToken });
        })
        .catch((err) => {
          console.warn('[DEV] Failed to refresh user:', err);
        });

      // Force-refetch the appConfig query — under `staleTime: Infinity`
      // it would otherwise stick with the cached payload.
      void queryClient.invalidateQueries({ queryKey: ['appConfig'] });
    }
  }, [user, queryClient]);

  // App config is owned by `useAppConfigQuery` (TanStack Query). It
  // auto-fetches when `isAuthenticated` flips true, dedupes across
  // ProtectedRoute mounts, and caches with `staleTime: Infinity` — so
  // there's no imperative fetch here and no module-level dedup flag.

  // Not authenticated - redirect to login.
  // Encode the originally-requested path+search in ?redirect= so SignIn
  // (which reads searchParams.get('redirect')) can honor it after auth, AND
  // pass state.from for in-app consumers. The querystring survives hard
  // refreshes, which state.from does not.
  if (!user) {
    // A session token exists but /auth/me hasn't resolved yet (e.g. a reload
    // with no cached seed) — wait rather than bounce to login, which would drop
    // the user on the sign-in page mid-fetch.
    // #447: if the bootstrap fetch FAILED (non-401), show the unreachable state
    // with retry instead of a blank page. A 401 never reaches here — the
    // client's 401 interceptor clears the session first, so hasSession is false
    // and we fall through to the Navigate below.
    if (hasSession && isLoadingUser) {
      if (userLoadError) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: vars.color.surface.muted }}>
            <UnreachableState
              message={meFailureMessage(userLoadError)}
              onRetry={retryMe}
              retryLabel={buttonLabels.neutral.retry}
            />
          </div>
        );
      }
      return null;
    }

    // #1111 (1111b): through authRouteHref, so the path is validated before
    // it goes on the URL (a hostile one is dropped, not carried).
    const originalPath = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={authRouteHref(paths.loginSignIn, returnAddress(originalPath))}
        state={{ from: location }}
        replace
      />
    );
  }

  // Effective level + default home derive from a single source of truth
  // (src/lib/defaultRoute.ts), shared with the context-switch flow so the
  // "where does this user belong" answer can't drift between the two.
  const effectiveLevel = getEffectiveLevel(user);

  // Default route: where this user belongs when they can't access a page
  const defaultRoute = getDefaultRoute(user);

  // Loop guard: if the user is already at the route we'd redirect them to,
  // returning <Navigate to={defaultRoute}> would re-mount this component and
  // re-fire the same redirect — infinite "Maximum update depth" loop. Render
  // nothing instead so the user sees a blank panel rather than crashing the
  // router. This is only reachable if a misconfigured route grants the user
  // access to defaultRoute itself with a level requirement they fail.
  const wouldLoop = defaultRoute === location.pathname;

  // Check required level.
  // #64: when the user has no context AND no effective level (null — zero
  // memberships), render the context chooser instead of bouncing to defaultRoute
  // or rendering null (the blank-screen bug). Users WITH a level who fail the
  // check still bounce as before.
  if (requiredLevel && (effectiveLevel === null || !requiredLevel.includes(effectiveLevel))) {
    // #1400: a login that belongs to no company has no context to choose;
    // it lands on the viewer dashboard instead of the chooser.
    if (isNoCompanyLogin(user)) {
      if (wouldLoop) return null;
      // keep-search-exempt: protected-route-no-company
      return <Navigate to={paths.viewerHome} replace />;
    }
    if (effectiveLevel === null && !user.context?.type) {
      return <ContextRequired />;
    }
    if (wouldLoop) return null;
    // keep-search-exempt: protected-route-level-denied
    return <Navigate to={defaultRoute} replace />;
  }

  // Check superuser requirement
  if (requireSuperuser && !isSuperuser) {
    if (wouldLoop) return null;
    // keep-search-exempt: protected-route-superuser-denied
    return <Navigate to={defaultRoute} replace />;
  }

  // Check staff requirement
  if (requireStaff && !isStaff) {
    if (wouldLoop) return null;
    // keep-search-exempt: protected-route-staff-denied
    return <Navigate to={defaultRoute} replace />;
  }

  // Check network context requirement
  if (requireNetworkContext && user.context?.type !== 'network') {
    if (wouldLoop) return null;
    // keep-search-exempt: protected-route-network-denied
    return <Navigate to={defaultRoute} replace />;
  }

  // Org/network feature entitlements (packets/checklists/contacts) intentionally
  // do NOT gate routes or visibility — they only affect SHARING (per-object
  // capabilities.canInvite, server-computed) and consumer VIEWING (server 404).
  // User level (admin/manager/viewer) is the only access concept that gates
  // routes; that's handled above via requiredLevel.

  return <>{children}</>;
}
